// Maps a CSS hex colour to a short human-readable "colour family" label.
// Used by:
//   - Studio save form (auto-derived chips below the title input)
//   - CatalogueCard (swatch + label, optional)
//
// Approach: convert hex → HSL, then pick the closest named bucket. The
// table is intentionally small — the chips are an orientation aid, not
// a precise naming engine.

export interface ColourFamily {
  /** Display label, e.g. "Mid blue". */
  label: string;
  /** Single-hue hex swatch to render in chips. */
  swatch: string;
}

const FAMILIES: { name: string; h: [number, number]; s: [number, number]; l: [number, number]; swatch: string }[] = [
  // Greys / neutrals first — they cover very low saturation.
  { name: 'Soft white',     h: [0, 360], s: [0, 0.12], l: [0.85, 1.0],  swatch: '#f3f1ed' },
  { name: 'Pale grey',      h: [0, 360], s: [0, 0.18], l: [0.62, 0.85], swatch: '#d2cec8' },
  { name: 'Stone',          h: [0, 360], s: [0, 0.20], l: [0.42, 0.62], swatch: '#8a847a' },
  { name: 'Charcoal',       h: [0, 360], s: [0, 0.20], l: [0.0, 0.42],  swatch: '#2b2823' },

  // Reds / pinks
  { name: 'Soft pink',      h: [330, 360], s: [0.15, 0.6], l: [0.62, 0.9], swatch: '#f6c7d3' },
  { name: 'Hot pink',       h: [320, 350], s: [0.55, 1.0], l: [0.42, 0.65], swatch: '#e75494' },
  { name: 'Crimson',        h: [340, 12],  s: [0.55, 1.0], l: [0.25, 0.5],  swatch: '#a01d3c' },
  { name: 'Rust',           h: [10, 35],  s: [0.45, 1.0], l: [0.32, 0.55], swatch: '#a35b2b' },

  // Oranges / yellows
  { name: 'Amber',          h: [25, 50],  s: [0.55, 1.0], l: [0.45, 0.65], swatch: '#d99030' },
  { name: 'Gold',           h: [45, 60],  s: [0.55, 1.0], l: [0.42, 0.62], swatch: '#cda43a' },
  { name: 'Pale yellow',    h: [50, 70],  s: [0.15, 0.5], l: [0.65, 0.9],  swatch: '#f1e3a8' },

  // Greens
  { name: 'Forest',         h: [90, 150], s: [0.45, 1.0], l: [0.18, 0.4],  swatch: '#2e5d3a' },
  { name: 'Olive',          h: [60, 95],  s: [0.25, 0.6], l: [0.3, 0.55],  swatch: '#6b6a30' },
  { name: 'Sage',           h: [80, 140], s: [0.15, 0.4], l: [0.55, 0.78], swatch: '#b5c9a4' },
  { name: 'Mint',           h: [140, 170], s: [0.2, 0.55], l: [0.65, 0.88], swatch: '#a8d8c2' },
  { name: 'Emerald',        h: [135, 165], s: [0.55, 1.0], l: [0.32, 0.55], swatch: '#2e9d6a' },
  { name: 'Teal',           h: [165, 195], s: [0.45, 1.0], l: [0.28, 0.5],  swatch: '#1f8073' },

  // Blues
  { name: 'Ice blue',       h: [185, 215], s: [0.18, 0.5], l: [0.65, 0.9],  swatch: '#bcdcec' },
  { name: 'Sky',            h: [195, 215], s: [0.45, 0.8], l: [0.55, 0.78], swatch: '#7cb6e0' },
  { name: 'Mid blue',       h: [200, 240], s: [0.45, 1.0], l: [0.35, 0.6],  swatch: '#3a6ea0' },
  { name: 'Cobalt',         h: [215, 245], s: [0.55, 1.0], l: [0.32, 0.55], swatch: '#274a8a' },
  { name: 'Navy',           h: [215, 255], s: [0.55, 1.0], l: [0.12, 0.32], swatch: '#172547' },

  // Purples
  { name: 'Lavender',       h: [255, 285], s: [0.18, 0.5], l: [0.62, 0.85], swatch: '#c9b7da' },
  { name: 'Violet',         h: [255, 285], s: [0.5, 1.0], l: [0.35, 0.58],  swatch: '#7d4ec0' },
  { name: 'Plum',           h: [285, 325], s: [0.45, 1.0], l: [0.22, 0.42], swatch: '#5c2a64' },
  { name: 'Magenta',        h: [295, 330], s: [0.55, 1.0], l: [0.42, 0.65], swatch: '#a8338b' },

  // Browns / earthy
  { name: 'Sand',           h: [20, 45],  s: [0.18, 0.5], l: [0.55, 0.78], swatch: '#d8c69a' },
  { name: 'Bronze',         h: [20, 45],  s: [0.35, 0.7], l: [0.25, 0.5],  swatch: '#7a5028' },
];

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function hexToHsl(hex: string): { h: number; s: number; l: number } {
  // Strip leading "#" and normalise 3-digit → 6-digit.
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) {
    h = h.split('').map((c) => c + c).join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) {
    return { h: 0, s: 0, l: 0.5 }; // safe fallback
  }
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let hue = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let rawHue = 0;
    if (max === r) rawHue = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) rawHue = (b - r) / d + 2;
    else if (max === b) rawHue = (r - g) / d + 4;
    hue = rawHue * 60;
  }
  return { h: hue, s, l };
}

/** Bucket a hex colour into the closest named family. Always returns one. */
export function hexToColourName(hex: string): ColourFamily {
  const { h, s, l } = hexToHsl(hex);
  let best: { idx: number; score: number } | null = null;
  for (let i = 0; i < FAMILIES.length; i++) {
    const f = FAMILIES[i];
    if (s < f.s[0] || s > f.s[1]) continue;
    if (l < f.l[0] || l > f.l[1]) continue;
    // Hue check: support wrap-around (e.g. crimson spans 340..12).
    let hueOk: boolean;
    if (f.h[0] <= f.h[1]) {
      hueOk = h >= f.h[0] && h <= f.h[1];
    } else {
      hueOk = h >= f.h[0] || h <= f.h[1];
    }
    if (!hueOk) continue;
    // Score = closeness to centre of the bucket (lower = better).
    const hCentre =
      f.h[0] <= f.h[1]
        ? (f.h[0] + f.h[1]) / 2
        : ((f.h[0] + f.h[1] + 360) / 2) % 360;
    let dh = Math.abs(h - hCentre);
    if (dh > 180) dh = 360 - dh;
    const sCentre = (f.s[0] + f.s[1]) / 2;
    const lCentre = (f.l[0] + f.l[1]) / 2;
    const score =
      (dh / 90) * 0.5 +
      (Math.abs(s - sCentre) / 0.5) * 0.25 +
      (Math.abs(l - lCentre) / 0.5) * 0.25;
    if (best === null || score < best.score) {
      best = { idx: i, score };
    }
  }
  // Fallback to "Mid blue" if nothing matched (defensive — shouldn't happen
  // because "Stone" / "Charcoal" cover saturation 0 across the lightness range).
  if (best === null) best = { idx: 3, score: 0 };
  const f = FAMILIES[best.idx];
  return { label: f.name, swatch: f.swatch };
}

/** Coarse lightness descriptor for hex colours. */
export function hexLightnessTier(hex: string): 'light' | 'mid' | 'dark' {
  const { l } = hexToHsl(hex);
  if (l < 0.33) return 'dark';
  if (l > 0.66) return 'light';
  return 'mid';
}

export function readableTextOn(hex: string): string {
  const { l } = hexToHsl(hex);
  return clamp(l, 0, 1) > 0.55 ? '#111111' : '#ffffff';
}

/** All families — used by Studio save form to show "auto-derived" chips. */
export const ALL_COLOUR_FAMILIES: ColourFamily[] = FAMILIES.map((f) => ({
  label: f.name,
  swatch: f.swatch,
}));
